import React, { useState, useContext, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import PropTypes from 'prop-types';

import {
  fetchClientFilter,
  fetchBusinessUnitFilter,
  fetchWorkplaceFilter,
  fetchCompaniesFilter,
} from '#/api/filterService';
import DashboardContext from '#/context';
import Autocomplete from '#/components/Autocomplete/index';
import Checkbox from '#/components/Checkbox/index';
import Calendar from '#/components/Calendar';
import Button from '#/components/Button';
import workplaceIcon from '#/assets/icons/workplace.svg';
import businessUnitIcon from '#/assets/icons/businessUnit.svg';
import clientIcon from '#/assets/icons/clients.svg';
import companyIcon from '#/assets/icons/company.svg';
import styles from './styles.module.css';

import { useTranslation } from "react-i18next";
import moment from 'moment/moment';

const icons = {
  client: clientIcon,
  businessUnit: businessUnitIcon,
  workplace: workplaceIcon,
  company: companyIcon,
};

function Filters({ isOpen, hide }) {
  const { setLocalFilters, date, setDate } = useContext(
    DashboardContext
  );
  const [currentFilters, setCurrentFilter] = useState([]);
  const [personTemplate, setPersonTemplate] = useState(false);

  const { t } = useTranslation();
  
  useEffect(() => {
    const currentFilters = localStorage.getItem('currentFilters');
    if (currentFilters) setCurrentFilter(JSON.parse(currentFilters));
  }, [])

  function handleSelect(item) {
    setCurrentFilter([...currentFilters, item]);
  }

  function showPersonTemplate(isChecked) {
    setPersonTemplate(isChecked);
  }

  function handleClose(id) {
    const newCurrentFitlers = currentFilters.filter(item => item.id !== id);
    setCurrentFilter(newCurrentFitlers);
  }

  function handleApply() {
    let obj = {
      businessUnitIds: [],
      clientIds: [],
      workplaceIds: [],
      companyIds: []
    };

    currentFilters.forEach(filter => {
      if (filter.type === 'client') {
        obj = {
          ...obj,
          clientIds: [...obj.clientIds, filter.id],
        };
      }
      if (filter.type === 'businessUnit') {
        obj = {
          ...obj,
          businessUnitIds: [...obj.businessUnitIds, filter.id],
        };
      }
      if (filter.type === 'workplace') {
        obj = {
          ...obj,
          workplaceIds: [...obj.workplaceIds, filter.id],
        };
      }
      if (filter.type === 'company') {
        obj = {
          ...obj,
          companyIds: [...obj.companyIds, filter.id],
        };
      }
    });

    obj.personTemplate = personTemplate;

    setLocalFilters(obj);
    localStorage.setItem('localFilters', JSON.stringify(obj));
    localStorage.setItem('currentFilters', JSON.stringify(currentFilters));
    hide();
  }

  function handleReset() {
    setCurrentFilter([]);
    setPersonTemplate(false);
    setLocalFilters({
      businessUnitIds: [],
      clientIds: [],
      companyIds: [],
      workplaceIds: [],
      personTemplate: false
    });
    setDate({
      startDate: moment().startOf('month'),
      endDate: moment(),
    })
    localStorage.clear();
  }

  return (
    <React.Fragment>
      <div className={[isOpen ? styles.open : '', styles.wrapper].join(' ')}>
        <div className={styles.entry}>
          <div className={styles.inputs}>
            <Autocomplete
              type="client"
              title={t('CLIENT')}
              placeholder={t('CLIENT_FILTER_LABEL')}
              icon={clientIcon}
              fetchItens={fetchClientFilter}
              onSelect={handleSelect}
              selectedItens={currentFilters}
            />
            <Autocomplete
              type="businessUnit"
              title={t('BUSINESS_UNIT')}
              placeholder={t('BUSINESS_UNIT_FILTER_LABEL')}
              icon={businessUnitIcon}
              fetchItens={fetchBusinessUnitFilter}
              onSelect={handleSelect}
              selectedItens={currentFilters}
            />
            <Autocomplete
              type="workplace"
              title={t('WORKPLACE')}
              placeholder={t('WORKPLACE_FILTER_LABEL')}
              icon={workplaceIcon}
              fetchItens={fetchWorkplaceFilter}
              onSelect={handleSelect}
              selectedItens={currentFilters}
            />
            <Autocomplete
              type="company"
              title={t('COMPANY')}
              placeholder={t('COMPANY_FILTER_LABEL')}
              icon={companyIcon}
              fetchItens={fetchCompaniesFilter}
              onSelect={handleSelect}
              selectedItens={currentFilters}
            />
            <Calendar date={{ date, setDate }} />
            <Checkbox id={1} label={t('SHOW_PEOPLE_WITH_FINGERPRINT_TEMPLATE_ONLY')} callBack={showPersonTemplate} isChecked={personTemplate}/>
          </div>
          <hr className={styles.hr} />
          <div className={styles.buttons}>
            <Button buttonType="secondary" action={handleReset}>
              {t('CLEAN_FILTERS')}
            </Button>
            <Button buttonType="primary" action={handleApply}>
              {t('APPLY_FILTERS')}
            </Button>
          </div>
        </div>
        <div className={styles.tags}>
          <TransitionGroup>
            {currentFilters.filter(f => typeof f === "object").map(filter => (
              <CSSTransition
                key={filter.id}
                timeout={200}
                classNames={{
                  enter: styles['move-enter'],
                  enterActive: styles['move-enter-active'],
                  exit: styles['move-exit'],
                  exitActive: styles['move-exit-active'],
                }}
              >
                <span className={styles.tag}>
                  <img
                    src={`${icons[filter.type]}`}
                    alt="icon"
                    className={styles.tagNameIcon}
                  />
                  <p className={styles.tagName}>{filter.name}</p>
                  <button
                    onClick={() => handleClose(filter.id)}
                    className={styles.tagButton}
                  />
                </span>
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
      </div>
      {isOpen && (
        <div
          className={styles.overlay}
          onClick={() => {
            handleApply();
            hide();
          }}
        />
      )}
    </React.Fragment>
  );
}

Filters.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  hide: PropTypes.func.isRequired,
};

export default Filters;
